import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useDashboardStore from '../../stores/dashboardStore';
import {
    TIMEZONES, GRADES, LANGUAGES, COFFEE_GOALS, DAYS_OF_WEEK,
    DAY_COLORS, getAvatarUrl, getChipStyle
} from '../../constants/dashboard';
import SelectionModal from './modals/SelectionModal';

const ProfileForm = () => {
    const { t, i18n } = useTranslation();

    // Store selectors
    const formData = useDashboardStore(s => s.formData);
    const setFormData = useDashboardStore(s => s.setFormData);
    const setFormField = useDashboardStore(s => s.setFormField);
    const isEditMode = useDashboardStore(s => s.isEditMode);
    const setIsEditMode = useDashboardStore(s => s.setIsEditMode);
    const message = useDashboardStore(s => s.message);
    const setMessage = useDashboardStore(s => s.setMessage);
    const imageError = useDashboardStore(s => s.imageError);
    const setImageError = useDashboardStore(s => s.setImageError);
    const savedSections = useDashboardStore(s => s.savedSections);
    const countries = useDashboardStore(s => s.countries);
    const cities = useDashboardStore(s => s.cities);
    const interests = useDashboardStore(s => s.interests);
    const countrySearch = useDashboardStore(s => s.countrySearch);
    const setCountrySearch = useDashboardStore(s => s.setCountrySearch);
    const newCityName = useDashboardStore(s => s.newCityName);
    const setNewCityName = useDashboardStore(s => s.setNewCityName);
    const modals = useDashboardStore(s => s.modals);
    const openModal = useDashboardStore(s => s.openModal);
    const closeModal = useDashboardStore(s => s.closeModal);
    const autoSaveProfile = useDashboardStore(s => s.autoSaveProfile);
    const toggleMultiSelect = useDashboardStore(s => s.toggleMultiSelect);
    const removeOtherInterest = useDashboardStore(s => s.removeOtherInterest);
    const handleAvatarChange = useDashboardStore(s => s.handleAvatarChange);
    const handleAddCity = useDashboardStore(s => s.handleAddCity);
    const fetchCities = useDashboardStore(s => s.fetchCities);

    // Keep ref in sync with formData to avoid stale closures in onBlur
    const formDataRef = useRef(formData);
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    // Local handlers
    const handleChange = useCallback((e) => {
        setFormField(e.target.name, e.target.value);
    }, [setFormField]);

    const handleInputBlur = useCallback((section) => {
        autoSaveProfile(formDataRef.current, section);
    }, [autoSaveProfile]);

    const handleSubmit = (e) => {
        e.preventDefault();
        autoSaveProfile(formDataRef.current, 'submit');
    };

    // Helper to get localized interest name
    const getLocalizedInterest = (nameEn, type) => {
        if (!interests || !interests[type]) return nameEn;
        const item = interests[type].find(i => i.name_en === nameEn);
        return item && i18n.language === 'ru' ? item.name_ru : nameEn;
    };

    // Calculate profile completion percentage
    const calculateProfileProgress = () => {
        const fields = [
            { value: formData.name },
            { value: formData.family },
            { value: formData.country },
            { value: formData.city },
            { value: formData.timezone },
            { value: formData.languages?.length > 0 },
            { value: formData.profession },
            { value: formData.professionalDesc },
            { value: formData.personalDesc },
            { value: formData.professionalInterests?.length > 0 || formData.otherProfessionalInterests },
            { value: formData.personalInterests?.length > 0 || formData.otherPersonalInterests },
            { value: formData.bestMeetingDays?.length > 0 },
        ];
        const completed = fields.filter(f => f.value).length;
        return Math.round((completed / fields.length) * 100);
    };

    const isProfileComplete = (data) => {
        return !!(data.name?.trim() && data.family?.trim());
    };

    // Body scroll lock when any profile modal is open
    useEffect(() => {
        const isAnyModalOpen =
            modals.language || modals.timezone || modals.days ||
            modals.country || modals.city ||
            modals.professionalInterests || modals.personalInterests;

        if (isAnyModalOpen) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }

        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [modals.language, modals.timezone, modals.days, modals.country, modals.city, modals.professionalInterests, modals.personalInterests]);

    // Fetch cities when country changes or city modal opens
    useEffect(() => {
        if (modals.city || (formData.country && formData.country.iso)) {
            fetchCities();
        }
    }, [formData.country, modals.city, fetchCities]);

    // Shared SVG icons
    const CheckSvg = () => (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const EditSvg = () => (
        <>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 8.5L1 11L3.5 10.5L9.75 4.25L7.75 2.25L1.5 8.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.75 4.25L7.75 2.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </>
    );

    const savedStyle = { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' };

    return (
        <div className="profile-section glass-card">
            {/* Profile Header */}
            <div className="profile-header">
                <h2 className="section-title section-title-no-margin">{t('dashboard.profile.title', 'Your Profile')}</h2>
                <button
                    className="add-language-btn"
                    onClick={() => {
                        if (isEditMode) {
                            if (isProfileComplete(formData)) {
                                setIsEditMode(false);
                                setMessage({ type: '', text: '' });
                            } else {
                                setMessage({ type: 'error', text: t('dashboard.profile.error_incomplete', 'Please fill in all mandatory fields before switching to view mode.') });
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                        } else {
                            setIsEditMode(true);
                        }
                    }}
                    style={{ padding: '0.5rem 1rem' }}
                >
                    {isEditMode ? t('dashboard.profile.view_mode', 'View Mode') : t('dashboard.profile.edit_mode', 'Edit Mode')}
                </button>
            </div>

            {message.text && message.type === 'error' && (
                <div className={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
                    {message.text}
                </div>
            )}

            {isEditMode ? (
                <form onSubmit={handleSubmit} className="profile-form">
                    {/* Progress Indicator */}
                    <div className="profile-progress">
                        <div className="progress-header">
                            <span className="progress-label">{t('dashboard.profile.completion', 'Profile Completion')}</span>
                            <span className="progress-percentage">{calculateProfileProgress()}%</span>
                        </div>
                        <div className="progress-bar-container">
                            <div className="progress-bar-fill" style={{ width: `${calculateProfileProgress()}%` }}></div>
                        </div>
                    </div>

                    {/* Avatar and Name Row */}
                    <div className="avatar-name-container">
                        {/* Avatar */}
                        <div className="avatar-upload">
                            <label className="avatar-wrapper">
                                <div className="avatar-preview">
                                    {formData.avatar && !imageError ? (
                                        <img
                                            src={getAvatarUrl(formData.avatar)}
                                            alt="Avatar"
                                            onError={() => setImageError(true)}
                                        />
                                    ) : (
                                        <span>👤</span>
                                    )}
                                </div>
                                <div className="avatar-overlay">
                                    <span>{t('dashboard.profile.upload_avatar', 'Upload Photo')}</span>
                                </div>
                                <input
                                    type="file"
                                    accept="image/png, image/jpeg, image/jpg"
                                    onChange={(e) => handleAvatarChange(e.target.files[0])}
                                    style={{ display: 'none' }}
                                />
                            </label>
                        </div>

                        {/* Name & Family */}
                        <div className="form-row name-family-row">
                            <div className="form-group">
                                <label>{t('dashboard.profile.name', 'Name')}</label>
                                <input
                                    type="text"
                                    name="name"
                                    className="form-control"
                                    value={formData.name}
                                    onChange={handleChange}
                                    onBlur={() => handleInputBlur('name')}
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('dashboard.profile.family', 'Family Name')}</label>
                                <input
                                    type="text"
                                    name="family"
                                    className="form-control"
                                    value={formData.family}
                                    onChange={handleChange}
                                    onBlur={() => handleInputBlur('family')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Location */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>{t('dashboard.profile.country', 'Country')}</label>
                            <div className="language-chips">
                                {formData.country && (
                                    <div className="chip">
                                        {formData.country.flag} {formData.country.name}
                                    </div>
                                )}
                                <button
                                    type="button"
                                    className={`add-language-btn ${savedSections.country ? 'saved' : ''}`}
                                    onClick={() => {
                                        openModal('country');
                                        setCountrySearch('');
                                    }}
                                    style={savedSections.country ? savedStyle : {}}
                                >
                                    {savedSections.country ? (
                                        <><CheckSvg />{t('common.saved', 'Saved')}</>
                                    ) : (
                                        <><EditSvg />{formData.country ? t('dashboard.profile.change_country', 'Change') : t('common.select', 'Select')}</>
                                    )}
                                </button>
                            </div>
                        </div>
                        {formData.country && (
                            <div className="form-group">
                                <label>{t('dashboard.profile.city', 'City')}</label>
                                <div className="language-chips">
                                    {formData.city && (
                                        <div className="chip">
                                            {formData.city.name}
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className={`add-language-btn ${savedSections.city ? 'saved' : ''}`}
                                        onClick={() => openModal('city')}
                                        style={savedSections.city ? savedStyle : {}}
                                    >
                                        {savedSections.city ? (
                                            <><CheckSvg />{t('common.saved', 'Saved')}</>
                                        ) : (
                                            <><EditSvg />{formData.city ? t('dashboard.profile.change_city', 'Change') : t('common.select', 'Select')}</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Country Modal */}
                    <SelectionModal
                        isOpen={modals.country}
                        onClose={() => closeModal('country')}
                        title={t('dashboard.profile.select_country', 'Select Country')}
                        footerLabel={t('common.cancel', 'Cancel')}
                    >
                        <input
                            type="text"
                            placeholder={t('dashboard.profile.search_country', 'Search country...')}
                            className="form-control"
                            style={{ marginBottom: '1rem', width: '100%' }}
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            autoFocus
                        />
                        <div className="language-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gridTemplateRows: 'auto', gridAutoFlow: 'row' }}>
                            {countries
                                .filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()))
                                .map(country => (
                                    <label key={country.id} className={`language-option ${formData.country?.id === country.id ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="country-modal"
                                            checked={formData.country?.id === country.id}
                                            onChange={() => {
                                                const newData = { ...formData, country: country, city: null };
                                                setFormData(newData);
                                                closeModal('country');
                                                setCountrySearch('');
                                                autoSaveProfile(newData, 'country');
                                            }}
                                            style={{ display: 'none' }}
                                        />
                                        <span>{country.flag} {country.name}</span>
                                        {formData.country?.id === country.id && <span className="check-icon">✓</span>}
                                    </label>
                                ))}
                        </div>
                    </SelectionModal>

                    {/* City Modal */}
                    <SelectionModal
                        isOpen={modals.city}
                        onClose={() => closeModal('city')}
                        title={t('dashboard.profile.select_city', 'Select City')}
                        footerLabel={t('common.cancel', 'Cancel')}
                    >
                        <div className="language-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gridTemplateRows: 'auto', gridAutoFlow: 'row' }}>
                            {cities.map(city => (
                                <label key={city.id} className={`language-option ${formData.city?.id === city.id ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="city-modal"
                                        checked={formData.city?.id === city.id}
                                        onChange={() => {
                                            const newData = { ...formData, city: city };
                                            setFormData(newData);
                                            closeModal('city');
                                            autoSaveProfile(newData, 'city');
                                        }}
                                        style={{ display: 'none' }}
                                    />
                                    <span>{city.name}</span>
                                    {formData.city?.id === city.id && <span className="check-icon">✓</span>}
                                </label>
                            ))}

                            {/* Show selected city if not in list (e.g. unapproved or just added) */}
                            {formData.city && !cities.find(c => c.id === formData.city.id) && (
                                <label key={formData.city.id} className="language-option selected">
                                    <input
                                        type="radio"
                                        name="city-modal"
                                        checked={true}
                                        readOnly
                                        style={{ display: 'none' }}
                                    />
                                    <span>{formData.city.name}</span>
                                    <span className="check-icon">✓</span>
                                </label>
                            )}

                            {cities.length === 0 && !formData.city && (
                                <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#888' }}>
                                    {t('dashboard.profile.no_cities', 'No cities found for this country.')}
                                </p>
                            )}
                        </div>

                        {/* Add City Section */}
                        <div style={{ marginTop: '1.5rem', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666', fontWeight: 600 }}>
                                {t('dashboard.profile.add_city_label', 'Add your City')}
                            </label>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t('dashboard.profile.add_city_placeholder', 'Enter city name')}
                                    value={newCityName}
                                    onChange={(e) => setNewCityName(e.target.value)}
                                    style={{ flex: 1 }}
                                />
                                <button
                                    type="button"
                                    className="save-btn"
                                    onClick={handleAddCity}
                                    style={{ marginTop: 0, padding: '0 1.5rem', whiteSpace: 'nowrap', alignSelf: 'stretch', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    disabled={!newCityName.trim()}
                                >
                                    {t('common.add', 'Add')}
                                </button>
                            </div>
                        </div>
                    </SelectionModal>

                    {/* Professional Interests Modal */}
                    <SelectionModal
                        isOpen={modals.professionalInterests}
                        onClose={() => closeModal('professionalInterests')}
                        title={t('dashboard.profile.select_professional_interests', 'Select Professional Interests')}
                        footerLabel={t('common.done', 'Done')}
                        onFooter={() => {
                            closeModal('professionalInterests');
                            autoSaveProfile(formDataRef.current, 'professionalInterests');
                        }}
                        contentStyle={{ maxWidth: '600px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
                        selectedSection={formData.professionalInterests?.length > 0 && (
                            <div className="modal-selected-section">
                                <h4 style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                                    {t('common.selected', 'Selected')} ({formData.professionalInterests?.length})
                                </h4>
                                <div className="language-chips">
                                    {formData.professionalInterests?.map(interest => (
                                        <div key={interest} className="chip" style={getChipStyle(interest)}>
                                            {getLocalizedInterest(interest, 'professional')}
                                            <button
                                                type="button"
                                                className="chip-remove"
                                                onClick={() => toggleMultiSelect('professionalInterests', interest)}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    >
                        {interests.categories.professional && interests.categories.professional.map(category => (
                            <div key={category.id} style={{ marginBottom: '1.5rem' }}>
                                <h4 style={{ fontSize: '1rem', color: '#666', marginBottom: '0.5rem', borderBottom: '1px solid #eee', paddingBottom: '0.25rem' }}>
                                    {i18n.language === 'ru' ? category.name_ru : category.name_en}
                                </h4>
                                <div className="language-grid">
                                    {interests.professional
                                        .filter(item => item.category === category.id)
                                        .map(item => {
                                            const displayName = i18n.language === 'ru' ? item.name_ru : item.name_en;
                                            const storedValue = item.name_en;
                                            return (
                                                <label key={item.id} className={`language-option ${(formData.professionalInterests || []).includes(storedValue) ? 'selected' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={(formData.professionalInterests || []).includes(storedValue)}
                                                        onChange={() => toggleMultiSelect('professionalInterests', storedValue)}
                                                        style={{ display: 'none' }}
                                                    />
                                                    {displayName}
                                                    {(formData.professionalInterests || []).includes(storedValue) && <span className="check-icon">✓</span>}
                                                </label>
                                            );
                                        })}
                                </div>
                            </div>
                        ))}
                    </SelectionModal>

                    {/* Personal Interests Modal */}
                    <SelectionModal
                        isOpen={modals.personalInterests}
                        onClose={() => closeModal('personalInterests')}
                        title={t('dashboard.profile.select_personal_interests', 'Select Personal Interests')}
                        footerLabel={t('common.done', 'Done')}
                        onFooter={() => {
                            closeModal('personalInterests');
                            autoSaveProfile(formDataRef.current, 'personalInterests');
                        }}
                        contentStyle={{ maxWidth: '600px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
                        selectedSection={formData.personalInterests?.length > 0 && (
                            <div className="modal-selected-section">
                                <h4 style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                                    {t('common.selected', 'Selected')} ({formData.personalInterests?.length})
                                </h4>
                                <div className="language-chips">
                                    {formData.personalInterests?.map(interest => (
                                        <div key={interest} className="chip" style={getChipStyle(interest)}>
                                            {getLocalizedInterest(interest, 'personal')}
                                            <button
                                                type="button"
                                                className="chip-remove"
                                                onClick={() => toggleMultiSelect('personalInterests', interest)}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    >
                        {interests.categories.personal && interests.categories.personal.map(category => (
                            <div key={category.id} style={{ marginBottom: '1.5rem' }}>
                                <h4 style={{ fontSize: '1rem', color: '#666', marginBottom: '0.5rem', borderBottom: '1px solid #eee', paddingBottom: '0.25rem' }}>
                                    {i18n.language === 'ru' ? category.name_ru : category.name_en}
                                </h4>
                                <div className="language-grid">
                                    {interests.personal
                                        .filter(item => item.category === category.id)
                                        .map(item => {
                                            const displayName = i18n.language === 'ru' ? item.name_ru : item.name_en;
                                            const storedValue = item.name_en;
                                            return (
                                                <label key={item.id} className={`language-option ${(formData.personalInterests || []).includes(storedValue) ? 'selected' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={(formData.personalInterests || []).includes(storedValue)}
                                                        onChange={() => toggleMultiSelect('personalInterests', storedValue)}
                                                        style={{ display: 'none' }}
                                                    />
                                                    {displayName}
                                                    {(formData.personalInterests || []).includes(storedValue) && <span className="check-icon">✓</span>}
                                                </label>
                                            );
                                        })}
                                </div>
                            </div>
                        ))}
                    </SelectionModal>

                    {/* Timezone */}
                    <div className="form-group">
                        <label>{t('dashboard.profile.timezone', 'Time Zone')}</label>
                        <div className="language-chips">
                            {formData.timezone && (
                                <div className="chip">
                                    {formData.timezone}
                                </div>
                            )}
                            <button
                                type="button"
                                className={`add-language-btn ${savedSections.timezone ? 'saved' : ''}`}
                                onClick={() => openModal('timezone')}
                                style={savedSections.timezone ? savedStyle : {}}
                            >
                                {savedSections.timezone ? (
                                    <><CheckSvg />{t('common.saved', 'Saved')}</>
                                ) : (
                                    <><EditSvg />{t('dashboard.profile.change_timezone', 'Change')}</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Timezone Modal */}
                    <SelectionModal
                        isOpen={modals.timezone}
                        onClose={() => closeModal('timezone')}
                        title={t('dashboard.profile.select_timezone', 'Select Time Zone')}
                        footerLabel={t('common.cancel', 'Cancel')}
                    >
                        <div className="language-grid">
                            {TIMEZONES.map(tz => (
                                <label key={tz} className={`language-option ${formData.timezone === tz ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="timezone-modal"
                                        checked={formData.timezone === tz}
                                        onChange={() => {
                                            const newData = { ...formData, timezone: tz };
                                            setFormData(newData);
                                            closeModal('timezone');
                                            autoSaveProfile(newData, 'timezone');
                                        }}
                                        style={{ display: 'none' }}
                                    />
                                    {tz}
                                    {formData.timezone === tz && <span className="check-icon">✓</span>}
                                </label>
                            ))}
                        </div>
                    </SelectionModal>

                    {/* Best Meeting Days */}
                    <div className="form-group">
                        <label>{t('dashboard.profile.best_days', 'Best Meeting Days')}</label>
                        <div className="language-chips">
                            {(formData.bestMeetingDays || []).map(day => (
                                <div key={day} className="chip" style={{ backgroundColor: DAY_COLORS[day] || '#f3f4f6' }}>
                                    {t(`days.${day}`, day)}
                                    <button
                                        type="button"
                                        className="chip-remove"
                                        onClick={() => toggleMultiSelect('bestMeetingDays', day)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                className={`add-language-btn ${savedSections.bestMeetingDays ? 'saved' : ''}`}
                                onClick={() => openModal('days')}
                                style={savedSections.bestMeetingDays ? savedStyle : {}}
                            >
                                {savedSections.bestMeetingDays ? (
                                    <><CheckSvg />{t('common.saved', 'Saved')}</>
                                ) : (
                                    <><EditSvg />{formData.bestMeetingDays?.length > 0 ? t('dashboard.profile.change_days', 'Change') : t('common.select', 'Select')}</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Days Modal */}
                    <SelectionModal
                        isOpen={modals.days}
                        onClose={() => closeModal('days')}
                        title={t('dashboard.profile.select_days', 'Select Days')}
                        footerLabel={t('common.done', 'Done')}
                        onFooter={() => {
                            closeModal('days');
                            autoSaveProfile(formDataRef.current, 'bestMeetingDays');
                        }}
                    >
                        <div className="language-grid" style={{ gridTemplateColumns: 'repeat(1, 1fr)', gridTemplateRows: 'auto', gridAutoFlow: 'row' }}>
                            {DAYS_OF_WEEK.map(day => (
                                <label key={day} className={`language-option ${(formData.bestMeetingDays || []).includes(day) ? 'selected' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={(formData.bestMeetingDays || []).includes(day)}
                                        onChange={() => toggleMultiSelect('bestMeetingDays', day)}
                                        style={{ display: 'none' }}
                                    />
                                    {t(`days.${day}`, day)}
                                    {(formData.bestMeetingDays || []).includes(day) && <span className="check-icon">✓</span>}
                                </label>
                            ))}
                        </div>
                    </SelectionModal>

                    {/* Languages */}
                    <div className="form-group">
                        <label>{t('dashboard.profile.languages', 'Languages')}</label>
                        <div className="language-chips">
                            {(formData.languages || []).map(lang => (
                                <div key={lang} className="chip">
                                    {lang}
                                    <button
                                        type="button"
                                        className="chip-remove"
                                        onClick={() => toggleMultiSelect('languages', lang)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                className={`add-language-btn ${savedSections.languages ? 'saved' : ''}`}
                                onClick={() => openModal('language')}
                                style={savedSections.languages ? savedStyle : {}}
                            >
                                {savedSections.languages ? (
                                    <><CheckSvg />{t('common.saved', 'Saved')}</>
                                ) : (
                                    <><EditSvg />{formData.languages?.length > 0 ? t('dashboard.profile.change_languages', 'Change') : t('common.select', 'Select')}</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Language Modal */}
                    <SelectionModal
                        isOpen={modals.language}
                        onClose={() => closeModal('language')}
                        title={t('dashboard.profile.select_languages', 'Select Languages')}
                        footerLabel={t('common.done', 'Done')}
                        onFooter={() => {
                            closeModal('language');
                            autoSaveProfile(formDataRef.current, 'languages');
                        }}
                    >
                        <div className="language-grid">
                            {LANGUAGES.map(lang => (
                                <label key={lang} className={`language-option ${(formData.languages || []).includes(lang) ? 'selected' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={(formData.languages || []).includes(lang)}
                                        onChange={() => toggleMultiSelect('languages', lang)}
                                        style={{ display: 'none' }}
                                    />
                                    {lang}
                                    {(formData.languages || []).includes(lang) && <span className="check-icon">✓</span>}
                                </label>
                            ))}
                        </div>
                    </SelectionModal>

                    {/* Profession & Grade */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>
                                {t('dashboard.profile.profession', 'Profession')}
                                {savedSections.profession && (
                                    <span style={{ color: '#166534', fontSize: '0.75rem', marginLeft: '0.5rem', fontWeight: 'normal' }}>
                                        ✓ {t('common.saved', 'Saved')}
                                    </span>
                                )}
                            </label>
                            <input
                                type="text"
                                name="profession"
                                className="form-control"
                                value={formData.profession}
                                onChange={handleChange}
                                onBlur={() => handleInputBlur('profession')}
                            />
                        </div>
                        <div className="form-group">
                            <label>
                                {t('dashboard.profile.grade', 'Grade')}
                                {savedSections.grade && (
                                    <span style={{ color: '#166534', fontSize: '0.75rem', marginLeft: '0.5rem', fontWeight: 'normal' }}>
                                        ✓ {t('common.saved', 'Saved')}
                                    </span>
                                )}
                            </label>
                            <select
                                name="grade"
                                className="form-control"
                                value={formData.grade}
                                onChange={(e) => {
                                    handleChange(e);
                                    const newData = { ...formData, grade: e.target.value };
                                    autoSaveProfile(newData, 'grade');
                                }}
                            >
                                {GRADES.map(grade => (
                                    <option key={grade} value={grade}>{grade}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* LinkedIn */}
                    <div className="form-group">
                        <label>
                            LinkedIn
                            {savedSections.linkedin && (
                                <span style={{ color: '#166534', fontSize: '0.75rem', marginLeft: '0.5rem', fontWeight: 'normal' }}>
                                    ✓ {t('common.saved', 'Saved')}
                                </span>
                            )}
                        </label>
                        <input
                            type="text"
                            name="linkedin"
                            className="form-control"
                            placeholder="https://linkedin.com/in/..."
                            value={formData.linkedin || ''}
                            onChange={handleChange}
                            onBlur={() => handleInputBlur('linkedin')}
                        />
                    </div>

                    {/* Professional Description */}
                    <div className="form-group">
                        <label>
                            {t('dashboard.profile.professional_desc', 'Professional Description')}
                            {savedSections.professionalDesc && (
                                <span style={{ color: '#166534', fontSize: '0.75rem', marginLeft: '0.5rem', fontWeight: 'normal' }}>
                                    ✓ {t('common.saved', 'Saved')}
                                </span>
                            )}
                        </label>
                        <textarea
                            name="professionalDesc"
                            className="form-control"
                            value={formData.professionalDesc}
                            onChange={handleChange}
                            onBlur={() => handleInputBlur('professionalDesc')}
                        />
                    </div>

                    {/* Personal Description */}
                    <div className="form-group">
                        <label>
                            {t('dashboard.profile.personal_desc', 'Personal Description')}
                            {savedSections.personalDesc && (
                                <span style={{ color: '#166534', fontSize: '0.75rem', marginLeft: '0.5rem', fontWeight: 'normal' }}>
                                    ✓ {t('common.saved', 'Saved')}
                                </span>
                            )}
                        </label>
                        <textarea
                            name="personalDesc"
                            className="form-control"
                            value={formData.personalDesc}
                            onChange={handleChange}
                            onBlur={() => handleInputBlur('personalDesc')}
                        />
                    </div>

                    {/* Professional Interests */}
                    <div className="form-group">
                        <label>{t('dashboard.profile.professional_interests', 'Professional Interests')}</label>
                        <div className="language-chips" style={{ marginBottom: '1rem' }}>
                            {formData.professionalInterests?.map(interest => (
                                <div key={interest} className="chip" style={getChipStyle(interest)}>
                                    {getLocalizedInterest(interest, 'professional')}
                                    <button
                                        type="button"
                                        className="chip-remove"
                                        onClick={() => toggleMultiSelect('professionalInterests', interest)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            {(formData.otherProfessionalInterests || '').split(/[,.;]+/).map(item => item.trim()).filter(item => item.length > 0).map((item, index) => (
                                <div key={`other-${index}`} className="chip" style={getChipStyle(item)}>
                                    {item}
                                    <button
                                        type="button"
                                        className="chip-remove"
                                        onClick={() => removeOtherInterest('otherProfessionalInterests', item)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                className={`add-language-btn ${savedSections.professionalInterests ? 'saved' : ''}`}
                                onClick={() => openModal('professionalInterests')}
                                style={savedSections.professionalInterests ? savedStyle : {}}
                            >
                                {savedSections.professionalInterests ? (
                                    <><CheckSvg />{t('common.saved', 'Saved')}</>
                                ) : (
                                    <><EditSvg />{t('dashboard.profile.change_interests', 'Change')}</>
                                )}
                            </button>
                        </div>
                        <input
                            type="text"
                            name="otherProfessionalInterests"
                            className="form-control"
                            placeholder={t('dashboard.profile.other_professional_interests_placeholder', 'Other professional interests...')}
                            value={formData.otherProfessionalInterests}
                            onChange={handleChange}
                            onBlur={() => handleInputBlur('otherProfessionalInterests')}
                        />
                        {savedSections.otherProfessionalInterests && (
                            <div style={{ color: '#166534', fontSize: '0.75rem', marginTop: '0.25rem', textAlign: 'right' }}>
                                ✓ {t('common.saved', 'Saved')}
                            </div>
                        )}
                    </div>

                    {/* Personal Interests */}
                    <div className="form-group">
                        <label>{t('dashboard.profile.personal_interests', 'Personal Interests')}</label>
                        <div className="language-chips" style={{ marginBottom: '1rem' }}>
                            {formData.personalInterests?.map(interest => (
                                <div key={interest} className="chip" style={getChipStyle(interest)}>
                                    {getLocalizedInterest(interest, 'personal')}
                                    <button
                                        type="button"
                                        className="chip-remove"
                                        onClick={() => toggleMultiSelect('personalInterests', interest)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            {(formData.otherPersonalInterests || '').split(/[,.;]+/).map(item => item.trim()).filter(item => item.length > 0).map((item, index) => (
                                <div key={`other-${index}`} className="chip" style={getChipStyle(item)}>
                                    {item}
                                    <button
                                        type="button"
                                        className="chip-remove"
                                        onClick={() => removeOtherInterest('otherPersonalInterests', item)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                className={`add-language-btn ${savedSections.personalInterests ? 'saved' : ''}`}
                                onClick={() => openModal('personalInterests')}
                                style={savedSections.personalInterests ? savedStyle : {}}
                            >
                                {savedSections.personalInterests ? (
                                    <><CheckSvg />{t('common.saved', 'Saved')}</>
                                ) : (
                                    <><EditSvg />{t('dashboard.profile.change_interests', 'Change')}</>
                                )}
                            </button>
                        </div>
                        <input
                            type="text"
                            name="otherPersonalInterests"
                            className="form-control"
                            placeholder={t('dashboard.profile.other_personal_interests_placeholder', 'Other personal interests...')}
                            value={formData.otherPersonalInterests}
                            onChange={handleChange}
                            onBlur={() => handleInputBlur('otherPersonalInterests')}
                        />
                        {savedSections.otherPersonalInterests && (
                            <div style={{ color: '#166534', fontSize: '0.75rem', marginTop: '0.25rem', textAlign: 'right' }}>
                                ✓ {t('common.saved', 'Saved')}
                            </div>
                        )}
                    </div>

                    {/* Coffee Goals */}
                    <div className="form-group">
                        <label>
                            {t('dashboard.profile.coffee_goals', 'Coffee Goals')}
                            {savedSections.coffeeGoals && (
                                <span style={{ color: '#166534', fontSize: '0.75rem', marginLeft: '0.5rem', fontWeight: 'normal' }}>
                                    ✓ {t('common.saved', 'Saved')}
                                </span>
                            )}
                        </label>
                        <div className="language-chips">
                            {COFFEE_GOALS.map(goal => {
                                const isSelected = (formData.coffeeGoals || []).includes(goal);
                                return (
                                    <label
                                        key={goal}
                                        className={`language-option ${isSelected ? 'selected' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleMultiSelect('coffeeGoals', goal)}
                                            style={{ display: 'none' }}
                                        />
                                        {goal}
                                        {isSelected && <span className="check-icon">✓</span>}
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                </form>
            ) : (
                // View Mode
                <div className="profile-view">
                    <div className="profile-view-header">
                        <div className="avatar-preview view-mode-avatar">
                            {formData.avatar && !imageError ? (
                                <img
                                    src={getAvatarUrl(formData.avatar)}
                                    alt="Profile"
                                    onError={() => setImageError(true)}
                                />
                            ) : (
                                <div className="avatar-placeholder">
                                    {formData.name?.[0]}
                                </div>
                            )}
                        </div>
                        <div className="profile-view-info">
                            <h3>{formData.name} {formData.family}</h3>
                            <div className="profile-header-info">
                                {/* Profession line - only show if has meaningful value */}
                                {formData.profession && formData.profession !== 'Prefer not to say' && (
                                    <p className="profile-subtitle">
                                        {formData.profession}
                                        {formData.grade && formData.grade !== 'Prefer not to say' && ` • ${formData.grade}`}
                                    </p>
                                )}

                                {/* Location line - City, Country • Timezone */}
                                {formData.country && (
                                    <div className="profile-location">
                                        <span style={{ fontSize: '1.2rem' }}>{formData.country.flag}</span>
                                        {formData.city && <span>{formData.city.name}, </span>}
                                        <span>{formData.country.name}</span>
                                        {formData.timezone && <span> • {formData.timezone.replace('Europe/', '').replace('_', ' ')}</span>}
                                    </div>
                                )}

                                {/* LinkedIn icon-only link */}
                                {formData.linkedin && (
                                    <a
                                        href={formData.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="profile-linkedin-icon"
                                        aria-label="LinkedIn Profile"
                                        title="LinkedIn Profile"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                        </svg>
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="profile-content-grid">
                        {formData.professionalDesc && (
                            <div className="profile-section-block">
                                <h4>{t('dashboard.profile.professional_desc')}</h4>
                                <p>{formData.professionalDesc}</p>
                            </div>
                        )}

                        {formData.personalDesc && (
                            <div className="profile-section-block">
                                <h4>{t('dashboard.profile.personal_desc')}</h4>
                                <p>{formData.personalDesc}</p>
                            </div>
                        )}

                        {(formData.professionalInterests?.length > 0 || formData.otherProfessionalInterests || formData.personalInterests?.length > 0 || formData.otherPersonalInterests) && (
                            <div className="profile-interests-grid">
                                {(formData.professionalInterests?.length > 0 || formData.otherProfessionalInterests) && (
                                    <div>
                                        <h4>{t('dashboard.profile.professional_interests')}</h4>
                                        <div className="language-chips" style={{ flexWrap: 'wrap' }}>
                                            {formData.professionalInterests?.map(item => (
                                                <span key={item} className="chip" style={getChipStyle(item)}>
                                                    {getLocalizedInterest(item, 'professional')}
                                                </span>
                                            ))}
                                            {formData.otherProfessionalInterests && formData.otherProfessionalInterests.split(/[,.;]+/).map(item => item.trim()).filter(item => item).map((item, index) => (
                                                <span key={`other-${index}`} className="chip" style={getChipStyle(item)}>
                                                    {item}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {(formData.personalInterests?.length > 0 || formData.otherPersonalInterests) && (
                                    <div>
                                        <h4>{t('dashboard.profile.personal_interests')}</h4>
                                        <div className="language-chips" style={{ flexWrap: 'wrap' }}>
                                            {formData.personalInterests?.map(item => (
                                                <span key={item} className="chip" style={getChipStyle(item)}>
                                                    {getLocalizedInterest(item, 'personal')}
                                                </span>
                                            ))}
                                            {formData.otherPersonalInterests && formData.otherPersonalInterests.split(/[,.;]+/).map(item => item.trim()).filter(item => item).map((item, index) => (
                                                <span key={`other-${index}`} className="chip" style={getChipStyle(item)}>
                                                    {item}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {formData.coffeeGoals && formData.coffeeGoals.length > 0 && (
                            <div className="profile-section-block">
                                <h4>{t('dashboard.profile.coffee_goals')}</h4>
                                <div className="language-chips" style={{ flexWrap: 'wrap' }}>
                                    {(Array.isArray(formData.coffeeGoals) ? formData.coffeeGoals : [formData.coffeeGoals]).map((goal, index) => (
                                        <span key={index} className="chip" style={getChipStyle(goal)}>
                                            {goal}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="profile-interests-grid">
                            {formData.languages?.length > 0 && (
                                <div>
                                    <h4>{t('dashboard.profile.languages')}</h4>
                                    <div className="language-chips" style={{ flexWrap: 'wrap' }}>
                                        {(formData.languages || []).map(lang => (
                                            <span key={lang} className="chip">{lang}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {formData.bestMeetingDays?.length > 0 && (
                                <div>
                                    <h4>{t('dashboard.profile.best_days')}</h4>
                                    <div className="language-chips" style={{ flexWrap: 'wrap' }}>
                                        {(formData.bestMeetingDays || []).map(day => (
                                            <span key={day} className="chip" style={{ backgroundColor: DAY_COLORS[day] || '#f3f4f6' }}>
                                                {t(`days.${day}`, day)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileForm;
